/**
 * P5-04: 错误上报系统
 * 捕获：window.onerror + unhandledrejection + React Error Boundary
 */

interface ErrorReport {
  type: 'js_error' | 'unhandledrejection' | 'resource_error' | 'react_error';
  message: string;
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  componentStack?: string;
  pagePath: string;
  userAgent: string;
  sessionId: string;
  timestamp: number;
}

const SESSION_ID = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const REPORT_URL = import.meta.env.VITE_MONITOR_URL || '/api/events';

function reportError(data: Omit<ErrorReport, 'pagePath' | 'userAgent' | 'sessionId' | 'timestamp'>) {
  const payload: ErrorReport = {
    ...data,
    pagePath: window.location.pathname,
    userAgent: navigator.userAgent,
    sessionId: SESSION_ID,
    timestamp: Date.now(),
  };

  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(REPORT_URL, JSON.stringify({ ...payload, event_type: 'error' }));
    } else {
      fetch(REPORT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, event_type: 'error' }),
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // 静默失败
  }
}

/** 初始化全局错误捕获 */
export function initErrorReporting() {
  if (typeof window === 'undefined') return;

  // JavaScript 运行时错误
  window.addEventListener('error', (event) => {
    reportError({
      type: 'js_error',
      message: event.message,
      stack: event.error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Promise 未处理 rejection
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError({
      type: 'unhandledrejection',
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });

  // 资源加载失败
  window.addEventListener('error', (event) => {
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'IMG' || target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
      reportError({
        type: 'resource_error',
        message: `Failed to load ${target.tagName}: ${(target as any).src || (target as any).href}`,
      });
    }
  }, true);
}

/** React Error Boundary 使用的上报函数 */
export function reportReactError(error: Error, componentStack: string) {
  reportError({
    type: 'react_error',
    message: error.message,
    stack: error.stack,
    componentStack,
  });
}

/** 手动上报自定义错误 */
export function reportCustomError(message: string, context?: Record<string, any>) {
  reportError({
    type: 'js_error',
    message: `${message} ${context ? JSON.stringify(context) : ''}`,
  });
}
